import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const PrivacyPolicy = () => {
  const { currentTheme } = useTheme();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();

  // Scroll to section function - для страниц Privacy и Terms будет перенаправлять на главную
  const scrollToSection = (sectionId) => {
    // Перенаправление на главную страницу с хэшем
    navigate(`/#${sectionId}`);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Content based on language
  const content = {
    ru: {
      title: 'Политика конфиденциальности',
      lastUpdated: 'Последнее обновление:',
      date: '29 апреля 2025 г.',
      introduction: 'Добро пожаловать на сайт MoviBobby. Мы ценим ваше доверие и стремимся защитить вашу конфиденциальность.',
      sections: [
        {
          title: '1. Собираемая информация',
          content: 'Мы собираем следующие типы информации:\n\n• Персональная информация: имя, email, номер телефона, которые вы предоставляете при заполнении форм.\n\n• Техническая информация: IP-адрес, тип браузера, посещаемые страницы, время посещения, операционная система и другие технические данные о вашем устройстве.'
        },
        {
          title: '2. Использование информации',
          content: 'Собранная информация используется для:\n\n• Предоставления запрошенных услуг\n\n• Улучшения нашего сайта и услуг\n\n• Коммуникации с вами\n\n• Отправки маркетинговых материалов (если вы дали согласие)'
        },
        {
          title: '3. Защита информации',
          content: 'Мы применяем современные технические и организационные меры безопасности для защиты вашей персональной информации от несанкционированного доступа, изменения, раскрытия или уничтожения.'
        },
        {
          title: '4. Сторонние сервисы',
          content: 'Мы можем использовать сторонние сервисы, такие как Google Analytics, которые могут собирать информацию о вашем использовании нашего сайта. Эти сервисы имеют собственные политики конфиденциальности.'
        },
        {
          title: '5. Права пользователей',
          content: 'Вы имеете право:\n\n• Получить информацию о хранящихся у нас ваших данных\n\n• Исправить неточную информацию\n\n• Удалить вашу информацию\n\n• Ограничить или возразить против обработки ваших данных\n\n• Получить копию ваших данных'
        },
        {
          title: '6. Изменения в политике',
          content: 'Мы можем обновлять нашу политику конфиденциальности. Рекомендуем периодически проверять эту страницу для ознакомления с изменениями.'
        },
        {
          title: '7. Контактная информация',
          content: 'Если у вас есть вопросы о нашей политике конфиденциальности, пожалуйста, свяжитесь с нами по электронной почте info@movibobby.com.'
        }
      ],
      back: 'Вернуться на главную',
      terms: 'Условия использования'
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated:',
      date: 'April 29, 2025',
      introduction: 'Welcome to MoviBobby website. We value your trust and are committed to protecting your privacy.',
      sections: [
        {
          title: '1. Information We Collect',
          content: 'We collect the following types of information:\n\n• Personal information: name, email, phone number that you provide when filling out forms.\n\n• Technical information: IP address, browser type, pages visited, time of visit, operating system, and other technical data about your device.'
        },
        {
          title: '2. How We Use Information',
          content: 'The collected information is used for:\n\n• Providing requested services\n\n• Improving our website and services\n\n• Communicating with you\n\n• Sending marketing materials (if you gave consent)'
        },
        {
          title: '3. Information Protection',
          content: 'We implement modern technical and organizational security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.'
        },
        {
          title: '4. Third-party Services',
          content: 'We may use third-party services such as Google Analytics, which may collect information about your use of our website. These services have their own privacy policies.'
        },
        {
          title: '5. User Rights',
          content: 'You have the right to:\n\n• Access information we hold about you\n\n• Correct inaccurate information\n\n• Delete your information\n\n• Restrict or object to processing of your data\n\n• Obtain a copy of your data'
        },
        {
          title: '6. Changes to the Policy',
          content: 'We may update our privacy policy. We recommend checking this page periodically to stay informed about changes.'
        },
        {
          title: '7. Contact Information',
          content: 'If you have any questions about our privacy policy, please contact us at info@movibobby.com.'
        }
      ],
      back: 'Back to home',
      terms: 'Terms of Service'
    }
  };

  // Get current language content
  const currentContent = content[currentLanguage] || content.ru;

  return (
    <div className={`${currentTheme === 'light' ? 'bg-light-background text-light-text' : 'bg-[#050505] text-white'} min-h-screen`}>
      {/* Navbar */}
      <Navbar activeSection="" scrollToSection={scrollToSection} />
      
      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mb-12 text-center"
          >
            <h1 className={`text-4xl font-bold mb-4 ${
              currentTheme === 'light' 
                ? 'bg-clip-text text-transparent bg-gradient-to-r from-light-primary to-light-secondary'
                : 'bg-clip-text text-transparent bg-gradient-to-r from-dark-primary to-dark-secondary'
            }`}>
              {currentContent.title}
            </h1>
            <p className={`${currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
              {currentContent.lastUpdated} {currentContent.date}
            </p>
          </motion.div>
          
          <div className={`max-w-4xl mx-auto ${
            currentTheme === 'light' 
              ? 'bg-white shadow-lg' 
              : 'bg-[#0A0A0A] border border-gray-800'
          } rounded-xl p-8`}>
            <motion.p 
              variants={fadeInUp}
              className={`text-lg mb-8 ${
                currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
              }`}
            >
              {currentContent.introduction}
            </motion.p>
            
            {currentContent.sections.map((section, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="mb-8"
              >
                <h2 className={`text-xl font-bold mb-4 ${
                  currentTheme === 'light' ? 'text-gray-800' : 'text-white'
                }`}>
                  {section.title}
                </h2>
                <div 
                  className={`${
                    currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                  } whitespace-pre-line`}
                >
                  {section.content}
                </div>
              </motion.div>
            ))}
            
            <div className="flex flex-col sm:flex-row justify-between items-center mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
              <Link 
                to="/"
                className={`mb-4 sm:mb-0 px-6 py-2 rounded-full ${
                  currentTheme === 'light'
                    ? 'bg-light-primary/10 text-light-primary hover:bg-light-primary/20'
                    : 'bg-dark-primary/10 text-dark-primary hover:bg-dark-primary/20'
                } transition-all duration-300`}
              >
                ← {currentContent.back}
              </Link>
              
              <Link 
                to="/terms"
                className={`px-6 py-2 rounded-full ${
                  currentTheme === 'light'
                    ? 'bg-light-secondary/10 text-light-secondary hover:bg-light-secondary/20'
                    : 'bg-dark-secondary/10 text-dark-secondary hover:bg-dark-secondary/20'
                } transition-all duration-300`}
              >
                {currentContent.terms} →
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 